import { type PortalIcon, resolveIconSrc } from "@/api/apps";
import { cn } from "@/lib/utils";
import { resolveTheme, useThemeStore } from "@/stores/theme";

/**
 * The fixed-size box a portal logo sits in (flowstack#1976).
 *
 * Every card gets the SAME well, and the image fills it under
 * `object-contain` rather than sitting at its natural size under a
 * `max-h`/`max-w` cap. A cap only ever shrinks: a PNG whose intrinsic size
 * is smaller than the box rendered smaller than its neighbours. Filling the
 * well scales every logo to the same bounding box, up or down.
 *
 * That box is the PNG's canvas, transparent margin included, so no sizing
 * here can compensate for an empty band baked into the file — which is how
 * the light-theme Affinity-Rxiv wordmark read as the odd one out. The
 * margins are trimmed in the assets themselves, and
 * `portal-logos.test.ts` pins every `public/portals/` logo's artwork to
 * fill its canvas.
 *
 * Theme pairing is `resolveIconSrc` (`path_dark` on dark, else `path`), the
 * same helper the launcher always used, so the Dashboard and the Apps page
 * cannot pick different files for one portal.
 *
 * `muted` is the locked-portal treatment: desaturated and dimmed, lifting on
 * the parent link's hover.
 */
export function LogoWell({
  icon,
  size = "md",
  muted = false,
}: {
  icon: PortalIcon;
  size?: "sm" | "md";
  muted?: boolean;
}): JSX.Element {
  const theme = useThemeStore((s) => s.theme);
  const src = resolveIconSrc(icon, resolveTheme(theme));
  return (
    <div
      data-testid="logo-well"
      className={cn(
        "flex w-full items-center justify-center",
        size === "md" ? "h-20 px-4" : "h-12 px-2",
        muted && "opacity-50 saturate-0 transition-all group-hover:opacity-70"
      )}
    >
      <img src={src} alt={icon.alt} className="h-full w-full object-contain" loading="lazy" />
    </div>
  );
}
